import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { AdsLibrary } from './AutoBingoAdmin.js';
import { finnishDate, type BingoAdPlayStat, type BingoAdView, type BingoAutoConfig, type BingoCarousel } from '../../../shared/bingo.js';

// The ad carousel (user request 2026-10-01): how the sponsor ads are played in the automatic bingo
// – order, counts at the start / end / per break, a maximum per game – the ads in the carousel with
// their order, weight and validity period, a new ad, and the play report for the sponsors.
// Ads on/off stays on the Automaattinen bingo tab.

type Flash = { ok: boolean; text: string } | null;
interface AutoView {
  config: BingoAutoConfig;
  ads: BingoAdView[];
}
const k = () => t.admin.carousel;
const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];
const seconds = (ms: number) => `${Math.round(ms / 100) / 10} s`;

export function AdCarousel(props: { onFlash: (f: Flash) => void; admin: boolean }) {
  const [view, setView] = useState<AutoView | null>(null);
  const [carousel, setCarousel] = useState<BingoCarousel | null>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<BingoAdView | null>(null);

  const apply = (v: AutoView) => {
    setView(v);
    setCarousel(v.config.carousel);
  };
  const load = () =>
    api<AutoView>('GET', '/api/admin/bingo/auto')
      .then(apply)
      .catch((err) => props.onFlash({ ok: false, text: errorText(err) }));
  useEffect(() => {
    void load();
  }, []);

  /** The settings are saved whole: the latest from the server with the carousel changes. */
  const saveConfig = async (change: Partial<BingoAutoConfig>, ok: string) => {
    setBusy(true);
    try {
      const fresh = await api<AutoView>('GET', '/api/admin/bingo/auto');
      apply(await api<AutoView>('PUT', '/api/admin/bingo/auto', { ...fresh.config, ...change }));
      props.onFlash({ ok: true, text: ok });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  const patchAd = async (ad: BingoAdView, change: Partial<Pick<BingoAdView, 'weight' | 'validFrom' | 'validUntil'>>) => {
    const next = { weight: ad.weight, validFrom: ad.validFrom, validUntil: ad.validUntil, ...change };
    try {
      apply(await api<AutoView>('PATCH', `/api/admin/bingo/ads/${ad.id}`, next));
      props.onFlash({ ok: true, text: k().adSaved(ad.name) });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  const remove = async (ad: BingoAdView) => {
    setRemoving(null);
    try {
      apply(await api<AutoView>('DELETE', `/api/admin/bingo/ads/${ad.id}`));
      props.onFlash({ ok: true, text: t.admin.autoBingo.adTexts.removed(ad.name) });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };

  if (!view || !carousel) return <p class="muted">{t.admin.autoBingo.loading}</p>;
  const c = carousel;
  const setC = <K extends keyof BingoCarousel>(key: K, value: BingoCarousel[K]) => setCarousel({ ...c, [key]: value });
  const num = (key: 'startCount' | 'endCount' | 'breakEvery' | 'perBreak' | 'maxPerGame') => ({
    value: c[key],
    onInput: (e: Event) => setC(key, Number((e.currentTarget as HTMLInputElement).value)),
  });
  const ids = view.config.adIds;
  const all = view.ads.filter((x) => x.kind === 'ad');
  const inCarousel = ids.map((id) => all.find((x) => x.id === id)).filter((x): x is BingoAdView => Boolean(x));
  const outside = all.filter((x) => !ids.includes(x.id));
  const today = finnishDate(Date.now());
  const move = (id: number, by: number) => {
    const list = inCarousel.map((x) => x.id);
    const at = list.indexOf(id);
    const to = at + by;
    if (at < 0 || to < 0 || to >= list.length) return;
    [list[at], list[to]] = [list[to]!, list[at]!];
    void saveConfig({ adIds: list }, k().orderSaved);
  };
  const include = (id: number, on: boolean) =>
    void saveConfig({ adIds: on ? [...inCarousel.map((x) => x.id), id] : inCarousel.map((x) => x.id).filter((x) => x !== id) }, k().listSaved);

  const row = (ad: BingoAdView, index: number | null) => {
    const expired = Boolean(ad.validUntil && ad.validUntil < today);
    const upcoming = Boolean(ad.validFrom && ad.validFrom > today);
    return (
      <li class={`user-row bingo-ad-row carousel-row ${index === null ? 'outside' : ''}`} key={ad.id}>
        {index !== null && (
          <div class="carousel-move">
            <button class="btn btn-small" disabled={busy || index === 0} onClick={() => move(ad.id, -1)} aria-label={k().up}>
              ▲
            </button>
            <span>{index + 1}.</span>
            <button class="btn btn-small" disabled={busy || index === inCarousel.length - 1} onClick={() => move(ad.id, 1)} aria-label={k().down}>
              ▼
            </button>
          </div>
        )}
        {ad.hasImage && <img class="bingo-ad-thumb" src={`/api/bingo/ads/${ad.id}/image`} alt="" />}
        <div class="user-main">
          <div class="user-name">
            {ad.name} {expired && <span class="badge badge-warn">{k().expired}</span>}
            {upcoming && <span class="badge badge-warn">{k().upcoming}</span>}
          </div>
          <div class="user-sub">{k().meta(seconds(ad.ms), ad.plays)}</div>
          <audio
            controls
            preload="none"
            src={`/api/bingo/ads/${ad.id}/audio`}
            ref={(el) => {
              if (el) el.volume = ad.volume;
            }}
          />
          <div class="carousel-fields">
            <label>
              {k().weight}
              <select value={ad.weight} onChange={(e) => void patchAd(ad, { weight: Number(e.currentTarget.value) })}>
                {Array.from({ length: 10 }, (_, i) => (
                  <option value={i + 1} key={i}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {k().from}
              <input type="date" value={ad.validFrom ?? ''} onChange={(e) => void patchAd(ad, { validFrom: e.currentTarget.value || null })} />
            </label>
            <label>
              {k().until}
              <input type="date" value={ad.validUntil ?? ''} onChange={(e) => void patchAd(ad, { validUntil: e.currentTarget.value || null })} />
            </label>
          </div>
        </div>
        <div class="user-actions">
          <button class="btn btn-small" disabled={busy} onClick={() => include(ad.id, index === null)}>
            {index === null ? `＋ ${k().add}` : k().take}
          </button>
          {props.admin && (
            <button class="btn btn-small btn-danger" onClick={() => setRemoving(ad)}>
              {t.admin.bingo.remove}
            </button>
          )}
        </div>
      </li>
    );
  };

  return (
    <>
      <form
        class="card form"
        onSubmit={(e) => {
          e.preventDefault();
          void saveConfig({ carousel: c }, k().saved);
        }}
      >
        <h2>{k().title}</h2>
        <p class="hint">{k().info}</p>
        {!view.config.ads && <p class="error">{k().adsOff}</p>}
        <fieldset class="card-format">
          <legend>{k().order}</legend>
          {(['sequence', 'random', 'weighted'] as const).map((o) => (
            <label class="radio" key={o}>
              <input type="radio" name="carousel-order" checked={c.order === o} onChange={() => setC('order', o)} />
              {k().orders[o]}
            </label>
          ))}
        </fieldset>
        <div class="grid-2">
          <label>
            {k().startCount}
            <input type="number" min={0} max={5} required {...num('startCount')} />
          </label>
          <label>
            {k().endCount}
            <input type="number" min={0} max={5} required {...num('endCount')} />
          </label>
        </div>
        <label class="switch">
          <input type="checkbox" checked={c.breaks} onChange={(e) => setC('breaks', e.currentTarget.checked)} />
          <span class="switch-track" aria-hidden="true" />
          {k().breaks}
        </label>
        {c.breaks && (
          <div class="grid-2">
            <label>
              {k().breakEvery}
              <input type="number" min={3} max={75} required {...num('breakEvery')} />
            </label>
            <label>
              {k().perBreak}
              <input type="number" min={1} max={5} required {...num('perBreak')} />
            </label>
          </div>
        )}
        <label>
          {k().maxPerGame}
          <input type="number" min={0} max={100} required {...num('maxPerGame')} />
        </label>
        <button class="btn btn-primary" type="submit" disabled={busy}>
          {k().save}
        </button>
      </form>

      <section class="card">
        <h2>{k().listTitle}</h2>
        <p class="hint">{k().listInfo}</p>
        {all.length === 0 ? (
          <p class="muted">{t.admin.autoBingo.adTexts.empty}</p>
        ) : (
          <>
            {inCarousel.length === 0 ? <p class="muted">{k().noneIn}</p> : <ul class="user-list">{inCarousel.map((ad, i) => row(ad, i))}</ul>}
            {outside.length > 0 && (
              <>
                <h3>{k().outsideTitle}</h3>
                <ul class="user-list">{outside.map((ad) => row(ad, null))}</ul>
              </>
            )}
          </>
        )}
      </section>

      <AdStats onFlash={props.onFlash} />

      {removing && (
        <ConfirmDialog
          title={t.admin.autoBingo.removeTitle(removing.name)}
          confirmLabel={t.admin.bingo.remove}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={() => void remove(removing)}
          onCancel={() => setRemoving(null)}
        >
          <p>{k().removeText}</p>
        </ConfirmDialog>
      )}
    </>
  );
}

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

/** The sponsor report: plays per ad in a period, also as a CSV file. */
function AdStats(props: { onFlash: (f: Flash) => void }) {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(finnishDate(Date.now()));
  const [stats, setStats] = useState<BingoAdPlayStat[] | null>(null);
  const range = () => {
    const start = new Date(`${from}T00:00`).getTime();
    const end = new Date(`${to}T00:00`).getTime() + 24 * 60 * 60 * 1000;
    return { start, end };
  };
  const show = async (e?: Event) => {
    e?.preventDefault();
    const { start, end } = range();
    if (!(start < end)) return;
    try {
      setStats((await api<{ stats: BingoAdPlayStat[] }>('GET', `/api/admin/bingo/ads/stats?from=${start}&to=${end}`)).stats);
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  useEffect(() => {
    void show();
  }, []);
  const when = (ms: number) => new Date(ms).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' });
  const csv = () => {
    if (!stats) return;
    const rows = [k().csvHead, ...stats.map((s) => [s.name.replaceAll(';', ','), s.plays, when(s.first), when(s.last)].join(';'))];
    const blob = new Blob([`﻿${rows.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `mainossoitot-${from}-${to}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  return (
    <form class="card form" onSubmit={show}>
      <h2>{k().statsTitle}</h2>
      <p class="hint">{k().statsInfo}</p>
      <div class="grid-2">
        <label>
          {k().from}
          <input type="date" value={from} onInput={(e) => setFrom(e.currentTarget.value)} required />
        </label>
        <label>
          {k().until}
          <input type="date" value={to} onInput={(e) => setTo(e.currentTarget.value)} required />
        </label>
      </div>
      <div class="bingo-start-actions">
        <button class="btn btn-primary" type="submit">
          {k().show}
        </button>
        <button class="btn" type="button" disabled={!stats || stats.length === 0} onClick={csv}>
          ⬇ {k().csv}
        </button>
      </div>
      {stats &&
        (stats.length === 0 ? (
          <p class="muted">{k().noPlays}</p>
        ) : (
          <table class="carousel-stats">
            <thead>
              <tr>
                <th>{k().colAd}</th>
                <th>{k().colPlays}</th>
                <th>{k().colFirst}</th>
                <th>{k().colLast}</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => (
                <tr key={`${s.adId}-${s.name}`}>
                  <td>
                    {s.name} {s.adId === null && <span class="muted">({k().removed})</span>}
                  </td>
                  <td>{s.plays}</td>
                  <td>{when(s.first)}</td>
                  <td>{when(s.last)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
    </form>
  );
}
